import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { RiShoppingCartLine } from "react-icons/ri";
import { FaHeart, FaRegHeart } from 'react-icons/fa6';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useState, memo } from 'react';
import type { Product } from '@/utils/supabase';

interface ProductCardProps {
  product: Product;
  loading?: 'lazy' | 'eager';
}

export const ProductCard = memo(function ProductCard({ product, loading = 'lazy' }: ProductCardProps) {
  const { t, currentLang } = useI18n();
  const addItem = useCartStore((state) => state.addItem);
  const toggleWishlist = useWishlistStore((state) => state.toggleItem);
  const isWishlisted = useWishlistStore((state) => state.isWishlisted(product.id));
  const [isAdded, setIsAdded] = useState(false);

  const name = currentLang === 'bn' ? product.name_bn : product.name_en;

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      quantity: 1,
      price: product.price,
      name: product.name_en,
      nameBn: product.name_bn,
      image: product.images[0] || '',
      weight: product.weight_grams,
      stock: product.stock_quantity,
    });

    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 1500);
  };

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist({
      productId: product.id,
      name: product.name_en,
      nameBn: product.name_bn,
      image: product.images[0] || '',
      price: product.price,
    });
  };

  const isOutOfStock = product.stock_quantity <= 0;
  const hasDiscount = product.compare_price && product.compare_price > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compare_price! - product.price) / product.compare_price!) * 100)
    : 0;

  return (
    <Card className="py-0 gap-0 group overflow-hidden border-none rounded-md shadow-[0px_2px_4px_rgba(0,0,0,0.25)] lg:shadow-[0px_2px_4px_rgba(0,0,0,0.35)] hover: transition-all duration-300">
      {/* Image */}
      <Link to={`/products/${product.id}`} className="block relative">
        <div className="aspect-square overflow-hidden bg-white">
          <img
            src={product.images[0] || '/placeholder-product.png'}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading={loading}
          />
        </div>

        {/* Badges */}
        <div className="absolute top-1.5 sm:top-2 left-1.5 sm:left-2 flex flex-row items-center gap-1 sm:gap-2">
          {product.is_featured && (
            <Badge className="bg-primary text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5">
              Featured
            </Badge>
          )}
        </div>

        {/* Wishlist Heart */}
        <button
          onClick={handleToggleWishlist}
          className="absolute top-1.5 sm:top-2 right-1.5 sm:right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-sm hover:scale-110 active:scale-95 transition-all z-10"
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          {isWishlisted ? (
            <FaHeart className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />
          ) : (
            <FaRegHeart className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />
          )}
        </button>

        {/* Stock Badge */}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-white rounded-md flex items-center justify-center">
            <Badge variant="secondary" className="text-xs sm:text-lg px-2 sm:px-4 py-1 sm:py-2">
              {t('stock.out_of_stock')}
            </Badge>
          </div>
        )}
      </Link>

      {/* Content */}
      <CardContent className="p-2.5 sm:p-4">
        <Link to={`/products/${product.id}`}>
          <div className="flex items-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
            <h3 className="font-semibold text-sm sm:text-lg hover:text-primary transition-colors truncate">
              {name}
            </h3>
            {hasDiscount && (
              <Badge className="bg-tb-accent rounded-full text-white h-4 sm:h-5 px-1 sm:px-1.5 text-[10px] sm:text-[12px] shrink-0">
                -{discountPercent}%
              </Badge>
            )}
          </div>
        </Link>

        {/* Description hidden by request */}

        {/* Meta */}
        <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-700 mb-1.5 sm:mb-3">
          <span>{product.weight_grams}g</span>
          <span>•</span>
          <span>{product.shelf_life_days} {currentLang === 'bn' ? 'দিন' : 'days'}</span>
        </div>

        {/* Price */}
        <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
          <span className="text-base sm:text-xl font-bold text-primary">
            ৳{product.price.toFixed(2)}
          </span>
          {hasDiscount && (
            <span className="text-xs sm:text-sm text-gray-600 line-through">
              ৳{product.compare_price?.toFixed(2)}
            </span>
          )}
        </div>

        <Button
          onClick={handleAddToCart}
          disabled={isOutOfStock}
          className={`w-full text-xs sm:text-sm h-8 sm:h-10 border-none transition-all ${!isAdded ? 'chocolate-beveled text-white' : ''}`}
          variant={isAdded ? 'secondary' : 'default'}
        >
          {isAdded ? (
            <>
              <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('success.added_to_cart')}</span>
              <span className="sm:hidden">Added to Cart</span>
            </>
          ) : (
            <>
              <RiShoppingCartLine className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('btn.add_to_cart')}</span>
              <span className="sm:hidden">Add to Cart</span>
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
});

